import type { RouteRecordRaw, RouteLocationNormalized } from 'vue-router';
import * as storageService from 'src/services/storage';

import type * as paymentTypes from 'src/types/payment';

function parsePackagesQuery(value: unknown): paymentTypes.Package[] {
  if (typeof value !== 'string' || value.length === 0) return [];
  try {
    const parsed = JSON.parse(value) as paymentTypes.Package[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

declare module 'vue-router' {
  interface RouteMeta {
    guestOnly?: boolean;
    isTab?: boolean;
    requiresAuth?: boolean;
    showFooter?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => (storageService.isLoggedIn() ? { name: 'explore' } : { name: 'auth-login' }),
  },
  {
    component: () => import('pages/account/AccountOptionsPage.vue'),
    meta: { isTab: true, requiresAuth: false },
    name: 'account',
    path: '/account',
  },
  {
    component: () => import('pages/account/MyInfoPage.vue'),
    meta: { requiresAuth: true },
    name: 'account-my-info',
    path: '/account/my-info',
  },
  {
    component: () => import('pages/account/payment/BookingPaymentSuccessPage.vue'),
    name: 'account-payment-booking-success',
    path: '/account/payment/booking-success',
  },
  {
    component: () => import('pages/account/payment/CheckoutPage.vue'),
    name: 'account-payment-checkout',
    path: '/account/payment/checkout',
    props: (route: RouteLocationNormalized) => ({
      amount: route.query.amount ? Number(route.query.amount) : undefined,
      egoOrderId: route.query.egoOrderId as string | undefined,
      operation: route.query.operation as string,
      packageId: route.query.packageId ? Number(route.query.packageId) : undefined,
      paymentMethod: route.query.paymentMethod as string,
      userId: route.query.userId ? Number(route.query.userId) : undefined,
    }),
  },
  {
    component: () => import('pages/account/payment/PaymentOptionsPage.vue'),
    name: 'account-payment-options',
    path: '/account/payment/options',
    props: (route: RouteLocationNormalized) => ({
      amount: route.query.amount ? Number(route.query.amount) : undefined,
      egoOrderId: route.query.egoOrderId as string | undefined,
      operation: route.query.operation as string,
      packages: parsePackagesQuery(route.query.packages),
      userId: route.query.userId ? Number(route.query.userId) : undefined,
    }),
  },
  {
    component: () => import('pages/account/payment/PaymentStatusPage.vue'),
    name: 'account-payment-status',
    path: '/account/payment/status/:paymentMethod',
    props: (route: RouteLocationNormalized) => ({
      checkoutId: route.query.id as string,
      paymentMethod: route.params.paymentMethod as string,
    }),
  },
  {
    component: () => import('pages/explore/BookPage.vue'),
    meta: { requiresAuth: true },
    name: 'explore-book',
    path: '/book/:bookingHash',
    props: (route: RouteLocationNormalized) => ({
      bookingHash: route.params.bookingHash || '',
      checkIn: route.query.checkIn || '',
      checkOut: route.query.checkOut || '',
      currency: route.query.currency || 'SAR',
      guests: route.query.guests || '',
      hotelId: route.query.hotelId || '',
      residency: route.query.residency || 'SA',
    }),
  },
  {
    name: 'booking-payment',
    path: '/book/:orderId/pay',
    redirect: (to) => ({
      name: 'account-payment-options',
      query: {
        amount: to.query.amount ? Number(to.query.amount) : undefined,
        egoOrderId: to.params.orderId,
        operation: 'add-booking',
      },
    }),
  },
  {
    component: () => import('pages/explore/BookingPaymentPage.vue'),
    meta: { requiresAuth: true },
    name: 'explore-book-payment',
    path: '/book/payment',
    props: (route: RouteLocationNormalized) => ({
      amount: route.query.amount ? Number(route.query.amount) : undefined,
      egoOrderId: (route.query.egoOrderId as string) || '',
      paymentLink: (route.query.paymentLink as string) || '',
      price: (route.query.price as string) || '',
    }),
  },
  {
    component: () => import('pages/bookings/BookingListPage.vue'),
    meta: { isTab: true, requiresAuth: true },
    name: 'bookings',
    path: '/bookings',
  },
  {
    component: () => import('pages/bookings/BookingDetailsPage.vue'),
    meta: { requiresAuth: true },
    name: 'bookings-details',
    path: '/bookings/:egoOrderId',
    props: (route: RouteLocationNormalized) => ({
      egoOrderId: route.params.egoOrderId || '',
    }),
  },
  {
    component: () => import('pages/account/ContactUsPage.vue'),
    meta: { requiresAuth: false },
    name: 'account-contact-us',
    path: '/contact-us',
  },
  {
    component: () => import('pages/explore/HotelResultsPage.vue'),
    meta: { requiresAuth: false },
    name: 'explore-results',
    path: '/hotels',
    props: (route: RouteLocationNormalized) => ({
      checkIn: route.query.checkIn || '',
      checkOut: route.query.checkOut || '',
      countryCode: route.query.countryCode || '',
      currency: route.query.currency || 'SAR',
      destinationLabel: route.query.destinationLabel || '',
      guests: route.query.guests || '',
      regionId: route.query.regionId || '',
      regionLanguage: route.query.regionLanguage || 'en',
      residency: route.query.residency || 'SA',
      type: route.query.type || '',
    }),
  },
  {
    component: () => import('pages/explore/HotelDetailsPage.vue'),
    meta: { requiresAuth: false },
    name: 'explore-hotel',
    path: '/hotels/:hotelId',
    props: (route: RouteLocationNormalized) => ({
      checkIn: route.query.checkIn || '',
      checkOut: route.query.checkOut || '',
      currency: route.query.currency || 'SAR',
      guests: route.query.guests || '',
      hotelId: route.params.hotelId || '',
      regionId: route.query.regionId || '',
      residency: route.query.residency || 'SA',
    }),
  },
  {
    component: () => import('pages/account/auth/LoginPage.vue'),
    meta: { guestOnly: true, showFooter: false },
    name: 'auth-login',
    path: '/login',
  },
  {
    component: () => import('pages/lucky-wheel/LuckyWheelPage.vue'),
    meta: { requiresAuth: true, showFooter: false },
    name: 'lucky-wheel',
    path: '/lucky-wheel',
    props: (route: RouteLocationNormalized) => ({
      amount: (route.query.amount as string) || '0',
      checkoutId: (route.query.checkoutId as string) || '',
      operationFor: (route.query.operationFor as string) || '',
      redirectName: (route.query.redirectName as string) || 'explore',
    }),
  },
  {
    component: () => import('pages/account/auth/OtpPage.vue'),
    meta: { guestOnly: true, showFooter: false },
    name: 'auth-otp',
    path: '/otp',
    props: (route: RouteLocationNormalized) => ({
      dialCode: (route.query.dialCode as string) || '',
      isRegistered: route.query.isRegistered === 'true',
      otpExpiry: (route.query.otpExpiry as string) || '',
      phone: (route.query.phone as string) || '',
    }),
  },
  {
    component: () => import('pages/account/PrivacyPolicyPage.vue'),
    meta: { requiresAuth: false },
    name: 'account-privacy-policy',
    path: '/privacy-policy',
  },
  {
    component: () => import('pages/account/auth/RegisterPage.vue'),
    meta: { guestOnly: true, showFooter: false },
    name: 'auth-register',
    path: '/register',
    props: (route: RouteLocationNormalized) => ({
      dialCode: (route.query.dialCode as string) || '',
      otp: (route.query.otp as string) || '',
      phone: (route.query.phone as string) || '',
    }),
  },
  {
    component: () => import('pages/explore/SearchPage.vue'),
    meta: { isTab: true, requiresAuth: false },
    name: 'explore',
    path: '/search',
  },
  {
    component: () => import('pages/explore/SearchFlightPage.vue'),
    meta: { isTab: true, requiresAuth: false },
    name: 'explore-flights',
    path: '/search/flights',
  },
  {
    component: () => import('pages/explore/LicenseRequestPage.vue'),
    meta: { requiresAuth: false },
    name: 'license-request',
    path: '/license-request',
  },
  {
    component: () => import('pages/explore/PackageRequestPage.vue'),
    meta: { requiresAuth: false },
    name: 'package-request',
    path: '/package-request',
    props: (route: RouteLocationNormalized) => ({
      packageId: (route.query.packageId as string) || '',
    }),
  },
  {
    component: () => import('pages/account/TermsAndConditionsPage.vue'),
    meta: { requiresAuth: false },
    name: 'account-terms-and-conditions',
    path: '/terms-and-conditions',
  },
  {
    component: () => import('pages/account/VoucherDownloadPage.vue'),
    meta: { requiresAuth: false },
    name: 'voucher-download',
    path: '/voucher/download',
    props: (route: RouteLocationNormalized) => ({
      accessToken: route.query.accessToken as string,
      egoOrderId: route.query.egoOrderId as string,
    }),
  },
  {
    component: () => import('pages/explore/VisaRequestPage.vue'),
    meta: { requiresAuth: false },
    name: 'visa-request',
    path: '/visa-request',
  },
  {
    component: () => import('pages/wallet/WalletPage.vue'),
    meta: { isTab: true, requiresAuth: true },
    name: 'wallet',
    path: '/wallet',
  },

  // Always leave this as last one
  {
    component: () => import('pages/ErrorNotFound.vue'),
    path: '/:catchAll(.*)*',
  },
];

export default routes;
